"use client";

import { XIcon } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useCallback } from "react";
import {
  CartQuantityControl,
  PageWrapper,
  ProductPrice,
  ProductPriceSwitch,
} from "@/components/core";
import { DirectionalChevron } from "@/components/core/DirectionalChevron";
import { ProductSpecificationTabs } from "@/components/features/shop/ProductSpecificationTabs/ProductSpecificationTabs";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  ProductImageCarousel,
  Skeleton,
} from "@/components/ui";
import { useAuth } from "@/context/auth";
import {
  checkVendorNotVerified,
  useAddToCart,
  useLoginRequired,
  useRemoveFromCart,
} from "@/hooks";
import { Link, useRouter } from "@/i18n/navigation";
import { $api } from "@/lib/api-client";
import { localizeName } from "@/lib/i18n";

interface ShopItemDetailProps {
  itemId: string;
}

export const ShopItemDetail = ({ itemId }: ShopItemDetailProps) => {
  const t = useTranslations("shop");
  const tCart = useTranslations("cart");
  const locale = useLocale();
  const router = useRouter();
  const { isLoggedIn } = useAuth();
  const handleLoginRequired = useLoginRequired();

  const item = $api.useQuery("get", "/product/{id}", {
    params: { path: { id: itemId } },
  });

  const productImages = item.data?.data.product_images || [];
  const productColors = item.data?.data.colors || [];
  const isService = item.data?.data.is_service;
  // Services have no stock — only products can be out of stock.
  const isUnavailable = !isService && (item.data?.data.quantity ?? 0) <= 0;

  // Query cart status for both products and services
  const cartItem = $api.useQuery(
    "get",
    "/cart/product/{productId}/quantity",
    { params: { path: { productId: itemId } } },
    { enabled: isLoggedIn },
  );

  const removeFromCart = useRemoveFromCart({
    onSuccess: () => {
      cartItem.refetch();
    },
  });

  // For quantity increment (no toast) - only used for products
  const incrementCart = useAddToCart({
    onSuccess: () => {
      cartItem.refetch();
    },
  });

  // For first add (with toast)
  const addToCartFirst = useAddToCart({
    showAddedToast: true,
    isService: isService,
    onSuccess: () => {
      cartItem.refetch();
    },
  });

  const vendorIsVerified = item.data?.data.vendor?.is_verified;

  const handleFirstAdd = useCallback(
    handleLoginRequired(() => {
      if (
        checkVendorNotVerified(
          vendorIsVerified,
          tCart("toasts.vendorNotVerified"),
        )
      )
        return;
      addToCartFirst.mutate({ body: { product_id: itemId } });
    }),
    [handleLoginRequired, addToCartFirst, itemId, vendorIsVerified, tCart],
  );

  const handleIncrement = useCallback(
    handleLoginRequired(() => {
      if (
        checkVendorNotVerified(
          vendorIsVerified,
          tCart("toasts.vendorNotVerified"),
        )
      )
        return;
      incrementCart.mutate({ body: { product_id: itemId } });
    }),
    [handleLoginRequired, incrementCart, itemId, vendorIsVerified, tCart],
  );

  // Filter out colors that don't have color details
  const availableColors = productColors.filter(
    (productColor) => productColor.color !== null,
  );

  // Map service_location from string[] to Array<{ name: string }>
  const serviceLocation =
    item.data?.data?.service_location?.map((name) => ({ name })) || undefined;

  // Map service_type to isOffline/isOnline booleans
  const isOffline = item.data?.data?.service_type?.includes("OFFLINE");
  const isOnline = item.data?.data?.service_type?.includes("ONLINE");

  // Map shipping cost type
  const shippingCostType = item.data?.data?.is_free_shipping
    ? "free"
    : "negotiable";

  // Map product_variants to specifications format
  const specifications =
    item.data?.data?.product_variants?.map((variant) => ({
      name: variant.name,
      value: variant.value,
    })) || undefined;

  // Loading skeleton — mirrors the real two-column layout (gallery + details +
  // tabs) so the page silhouette holds steady while data loads.
  if (item.isLoading) {
    return (
      <PageWrapper>
        <div className="space-y-3">
          {/* Breadcrumb + close */}
          <div className="flex items-center justify-between">
            <div className="flex space-x-0.5">
              <Skeleton variant="muted" className="h-4 w-20" />
              <Skeleton variant="muted" className="h-4 w-16" />
              <Skeleton variant="muted" className="h-4 w-12" />
            </div>
            <Button size={"icon"} variant={"light"} onClick={router.back}>
              <XIcon className="text-text-secondary" />
            </Button>
          </div>

          <div className="md:grid md:grid-cols-2 md:gap-8 md:items-start">
            {/* LEFT: gallery (main image + thumbnail strip) */}
            <div className="space-y-3">
              <Skeleton
                size={null}
                variant="secondary"
                className="mx-auto aspect-square w-full max-w-[440px] rounded-sm"
              />
              <div className="flex justify-center gap-2 md:justify-start">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton
                    key={i}
                    variant="secondary"
                    className="size-14 shrink-0 rounded-md"
                  />
                ))}
              </div>
            </div>

            {/* RIGHT: title, colors, price, vendor */}
            <div className="mt-3 space-y-4 md:mt-0">
              <Skeleton variant="muted" className="h-5 w-3/4" />

              <div className="space-y-2">
                <Skeleton variant="muted" className="h-4 w-12" />
                <div className="flex items-center space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton
                      key={i}
                      variant="secondary"
                      className="size-8 rounded-full"
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Skeleton variant="muted" className="h-5 w-20" />
                <Skeleton variant="muted" className="h-6 w-28" />
              </div>

              <div className="space-y-3 rounded-sm bg-bg-secondary p-3">
                <Skeleton variant="muted" className="h-4 w-16" />
                <div className="flex items-center gap-4">
                  <Skeleton
                    variant="secondary"
                    className="size-10 rounded-full"
                  />
                  <Skeleton variant="muted" className="h-4 w-32" />
                </div>
              </div>
            </div>
          </div>

          {/* Tabs card */}
          <div className="mt-6 md:mt-8 md:rounded-xl md:border md:border-stroke-subtle md:bg-bg-secondary md:p-6 lg:p-8">
            <div className="flex gap-4">
              <Skeleton variant="muted" className="h-5 w-16" />
              <Skeleton variant="muted" className="h-5 w-16" />
              <Skeleton variant="muted" className="h-5 w-16" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton
                  key={i}
                  variant="secondary"
                  className="h-16 rounded-md"
                />
              ))}
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      bottomChildren={
        item.data?.data ? (
          <>
            <div className="mt-6 border-t-3 border-b-3 border-stroke-light px-4 py-6 md:mt-8 md:border-0 md:px-6 md:py-0 lg:px-8">
              <div className="mx-auto w-full max-w-screen-xl md:rounded-xl md:border md:border-stroke-subtle md:bg-bg-secondary md:p-6 lg:p-8">
                <ProductSpecificationTabs
                  isService={isService}
                  description={item.data?.data.description || undefined}
                  fullDescription={
                    item.data?.data.full_description || undefined
                  }
                  specifications={specifications}
                  shippingCostType={shippingCostType}
                  serviceTime={item.data?.data.service_time || undefined}
                  serviceLocation={serviceLocation}
                  isOffline={isOffline}
                  isOnline={isOnline}
                  showShippingCostType={false}
                  productId={itemId}
                />
              </div>
            </div>
            <div className="fixed bottom-0 left-0 right-0 bg-bg-primary border-t border-stroke-subtle">
              <div className="mx-auto flex max-w-screen-xl items-center justify-between gap-3 p-4 md:px-8">
                {!isUnavailable ? (
                  <>
                    {/* For products: show quantity controls if already in cart */}
                    {/* For services: show Remove from Cart button if in cart, Add to Cart otherwise */}
                    {!isService &&
                    isLoggedIn &&
                    cartItem.data?.data?.quantity ? (
                      <CartQuantityControl
                        size="sm"
                        quantity={cartItem.data?.data?.quantity as number}
                        max={item.data?.data.quantity}
                        incrementLoading={incrementCart.isPending}
                        onIncrement={handleIncrement}
                        decrementLoading={removeFromCart.isPending}
                        onDecrement={() => {
                          removeFromCart.mutate({
                            params: {
                              path: { id: cartItem.data?.data.id as string },
                            },
                          });
                        }}
                        className="border-stroke-light"
                      />
                    ) : isService && isLoggedIn && cartItem.data?.data?.id ? (
                      <Button
                        variant="bordered"
                        colors="secondary"
                        onClick={() => {
                          removeFromCart.mutate({
                            params: {
                              path: { id: cartItem.data?.data.id as string },
                            },
                          });
                        }}
                        loading={removeFromCart.isPending}
                      >
                        {t("itemDetail.removeFromCart")}
                      </Button>
                    ) : (
                      <Button
                        variant="solid"
                        colors="secondary"
                        onClick={handleFirstAdd}
                        loading={addToCartFirst.isPending}
                      >
                        {t("itemDetail.addToCart")}
                      </Button>
                    )}
                    <ProductPrice
                      seller_share_unit={
                        item.data!.data.price.seller_share_unit
                      }
                      seller_share_currency={
                        item.data!.data.price.seller_share_currency
                      }
                      seller_settlement_currency={
                        (
                          item.data!.data.price as {
                            seller_settlement_currency?: "UVM" | "USDT";
                          }
                        )?.seller_settlement_currency
                      }
                      bv={item.data!.data.price.bv}
                      className="text-base text-text-primary"
                    />
                  </>
                ) : null}
              </div>
            </div>
          </>
        ) : null
      }
    >
      <div className="space-y-3">
        {/* breadcrumb + close — full width */}
        <div className="flex items-center justify-between">
          <div className="flex underline text-text-secondary space-x-0.5">
            <Link className="whitespace-nowrap" href="/">
              {t("breadcrumbHome")}
            </Link>
            {localizeName(item.data?.data.category?.parent?.name, locale) && (
              <span className="whitespace-nowrap">
                {localizeName(item.data?.data.category?.parent?.name, locale)}/
              </span>
            )}
            <span className="whitespace-nowrap">
              {localizeName(item.data?.data.category?.name, locale)}
            </span>
          </div>
          <Button size={"icon"} variant={"light"} onClick={router.back}>
            <XIcon className="text-text-secondary" />
          </Button>
        </div>

        <div className="md:grid md:grid-cols-2 md:gap-8 md:items-start">
          {/* LEFT: gallery */}
          <div>
            {productImages.length > 0 && (
              <ProductImageCarousel
                images={productImages.map((img) => ({
                  url: img.url,
                  id: img.id,
                }))}
                alt={item.data?.data.title || "Product image"}
                videoUrl={item.data?.data.video_url || undefined}
              />
            )}
          </div>

          {/* RIGHT: details */}
          <div className="space-y-3 mt-3 md:mt-0">
            <h3 className="text-text-primary text-sm">
              {item.data?.data.title}
            </h3>

            {/* Colors - only show for products */}
            {!isService && availableColors.length > 0 && (
              <div className="space-y-2">
                <p className="text-text-primary">{t("itemDetail.color")}</p>
                <div className="flex items-center space-x-3">
                  {availableColors.map((productColor) => {
                    const color = productColor.color;
                    if (!color) return null;
                    return (
                      <div
                        key={productColor.id}
                        className="size-8 rounded-full border border-stroke-light"
                        style={{ backgroundColor: color.code }}
                        title={localizeName(color.name, locale)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              {isUnavailable ? (
                <div className="bg-semantic-error/20 text-semantic-error pt-7 pb-6 px-3 w-full rounded-sm">
                  <p className="text-base font-semibold">
                    {t("itemDetail.unavailable")}
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-text-primary font-semibold text-base mt-0.5">
                    {t("itemDetail.currentPrice")}
                  </p>
                  <ProductPriceSwitch
                    seller_share_unit={item.data!.data.price.seller_share_unit}
                    seller_share_currency={
                      item.data!.data.price.seller_share_currency
                    }
                    seller_settlement_currency={
                      (
                        item.data!.data.price as {
                          seller_settlement_currency?: "UVM" | "USDT";
                        }
                      )?.seller_settlement_currency
                    }
                    bv={item.data!.data.price.bv}
                  />
                </>
              )}
            </div>

            <div className="p-3 bg-bg-secondary rounded-sm space-y-3">
              <p className="text-base font-semibold">
                {t("itemDetail.seller")}
              </p>
              <Link
                href={`/shops/${item.data!.data.vendor_id}`}
                className="flex gap-4 items-center"
              >
                <Avatar className="size-10 border-2 border-white">
                  <AvatarImage
                    className="object-cover"
                    src={item.data!.data.vendor?.logo_url || ""}
                  />
                  <AvatarFallback>
                    {item.data!.data.vendor?.shop_name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <p className="flex items-center gap-1 text-text-primary text-sm font-semibold">
                  <span className="mt-0.5">
                    {item.data!.data.vendor?.shop_name}
                  </span>
                  <DirectionalChevron size={16} />
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};
